import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText,
  Calculator,
  PieChart,
  FileCheck,
  Building2,
  TrendingUp,
  BookOpen,
  Scale,
  Briefcase,
  ArrowRight,
  Quote,
  ExternalLink,
  Landmark,
  Newspaper,
  Calendar,
} from "lucide-react";

const HomePage = () => {
  const teamMembers = [
    {
      name: "Alessandra Noli",
      role: "Founder",
      credentials: "Tributarista qualificata e certificata UNI11511 L. 4/2013",
      image:
        "https://customer-assets.emergentagent.com/job_5d0931a5-7fce-44d9-a6df-94d957cb8348/artifacts/xzd0r9d4_ALESSANDRA_NOLI.avif",
    },
    {
      name: "Jessica Dri",
      role: "Tributarista",
      credentials:
        "Tributarista qualificata UNI11511 L. 4/2013, Dott.ssa in Economia Aziendale",
      image:
        "https://customer-assets.emergentagent.com/job_5d0931a5-7fce-44d9-a6df-94d957cb8348/artifacts/mubvhhc2_JESSICA_DRI.avif",
    },
    {
      name: "Mayra Dri",
      role: "Collaboratrice",
      credentials:
        "Diplomata in Amministrazione, Finanza e Marketing; Dott.ssa in Economia Aziendale",
      image:
        "https://customer-assets.emergentagent.com/job_5d0931a5-7fce-44d9-a6df-94d957cb8348/artifacts/ngtt6bax_mayra_DRI.avif",
    },
  ];

  const services = [
    {
      icon: FileText,
      title: "Fatturazione Elettronica",
      description: "Digitalizzazione aziendale e gestione fatture elettroniche",
    },
    {
      icon: Calculator,
      title: "Consulenza Fiscale",
      description: "Assistenza qualificata per imprese e privati",
    },
    {
      icon: Scale,
      title: "Consulenza Tributaria",
      description: "Pianificazione e ottimizzazione fiscale",
    },
    {
      icon: FileCheck,
      title: "Dichiarazioni Fiscali",
      description: "730, Modello Unico, IVA e altre dichiarazioni",
    },
    {
      icon: PieChart,
      title: "Controllo di Gestione",
      description: "Analisi e monitoraggio delle performance aziendali",
    },
    {
      icon: TrendingUp,
      title: "Pianificazione Fiscale",
      description: "Strategie per l'ottimizzazione del carico fiscale",
    },
  ];

  const partners = [
    {
      name: "Dott. Dino Fabris",
      role: "Dottore Commercialista e Revisore Contabile",
    },
    {
      name: "Dott. Pierluigi Sergio",
      role: "Dottore Commercialista e Revisore Contabile",
    },
    {
      name: "Dott. Stefano Sassara",
      role: "Consulente del Lavoro",
    },
  ];

  const blogPosts = [
    {
      title: "Come cambiano le rateizzazioni con l'Agenzia delle Entrate dal 2025?",
      excerpt: "Novità importanti per i contribuenti in difficoltà...",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
    },
    {
      title: "Dal Fatturato all'Utile",
      excerpt: "Come analizzare correttamente i risultati aziendali...",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400",
    },
    {
      title: "Fondi Pensione e Trattamento Fiscale",
      excerpt: "Tutto quello che c'è da sapere sulla previdenza complementare...",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center bg-brand-primary"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary via-brand-primary to-[#4E342E]" />
          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-brand-accent/10 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-2xl">
            <div className="flex items-start gap-4 mb-6 animate-fade-in">
              <Quote className="text-white/40 flex-shrink-0 mt-1" size={32} />
              <blockquote className="text-white/80 text-lg md:text-xl italic font-light leading-relaxed">
                "Il caso non esiste, e ciò che ci sembra casuale scaturisce
                dalle fonti più profonde"
              </blockquote>
            </div>
            <p className="text-white/60 text-sm mb-8 animate-fade-in stagger-1">
              — Friedrich Schiller
            </p>

            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-slide-up stagger-2">
              Metodo Contabile
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8 leading-relaxed animate-slide-up stagger-3">
              Studio tributarista e di consulenza fiscale.
              <br />
              Una realtà tutta al femminile dal 1998.
            </p>

            <div className="flex flex-wrap gap-4 animate-slide-up stagger-4">
              <Link to="/contatti">
                <Button
                  size="lg"
                  className="bg-white text-brand-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-primary"
                >
                  Richiedi Consulenza
                </Button>
              </Link>
              <Link to="/servizi">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="hero-cta-secondary"
                >
                  Scopri i Servizi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Chi Siamo Section */}
      <section
        id="chi-siamo"
        className="py-20 md:py-32 bg-white"
        data-testid="chi-siamo-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
                Chi Siamo
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4 mb-6">
                Passione, professionalità e attenzione al cliente
              </h2>
              <p className="text-brand-muted text-lg leading-relaxed mb-6">
                Passione, professionalità e attenzione al cliente
                contraddistinguono la Metodo Contabile fin dal 1998.
              </p>
              <p className="text-brand-muted leading-relaxed mb-6">
                Siamo una realtà tutta al femminile sempre al passo con i tempi
                che fornisce servizi professionali nel campo della consulenza
                fiscale, tributaria, amministrativa contabile e gestionale.
                Offriamo servizi di elaborazione dati contabili anche con
                supporto operativo in outsourcing.
              </p>
              <p className="text-brand-muted leading-relaxed">
                Grazie alle tecnologie digitali utilizzate in Studio, siamo in
                grado di seguire il cliente anche a distanza offrendo consulenza
                e supporto online.
              </p>
            </div>
            <div className="relative pb-20">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_5d0931a5-7fce-44d9-a6df-94d957cb8348/artifacts/spkcp30k_METODO_CONTABILE.avif"
                  alt="Team Metodo Contabile - Alessandra, Jessica e Mayra"
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-brand-primary text-white px-8 py-4 rounded-xl shadow-xl flex items-center gap-4">
                <p className="font-heading text-3xl font-bold">25+</p>
                <p className="text-white/80 text-sm">Anni di Esperienza</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section
        id="team"
        className="py-20 md:py-32 bg-brand-secondary"
        data-testid="team-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Il Nostro Team
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4">
              Professioniste al tuo servizio
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {teamMembers.map((member, index) => (
              <div
                key={member.name}
                className="team-card bg-white rounded-2xl overflow-hidden shadow-lg"
                data-testid={`team-card-${index}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-brand-foreground">
                    {member.name}
                  </h3>
                  <p className="text-brand-primary font-medium mt-1">
                    {member.role}
                  </p>
                  <p className="text-brand-muted text-sm mt-3 leading-relaxed">
                    {member.credentials}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section
        className="py-20 md:py-32 bg-white"
        data-testid="services-preview-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              I Nostri Servizi
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4">
              Soluzioni per ogni esigenza
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card
                key={service.title}
                className="service-card border-brand-border hover:border-brand-primary/30 bg-white"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center mb-4">
                    <service.icon className="text-brand-primary" size={24} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-2">
                    {service.title}
                  </h3>
                  <p className="text-brand-muted text-sm">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servizi">
              <Button
                size="lg"
                className="bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full px-8"
                data-testid="services-cta"
              >
                Vedi Tutti i Servizi
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section
        id="partners"
        className="py-20 md:py-32 bg-brand-secondary"
        data-testid="partners-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Partners
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4">
              I nostri collaboratori
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <Card
                key={partner.name}
                className="bg-white border-brand-border text-center"
                data-testid={`partner-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-brand-foreground">
                    {partner.name}
                  </h3>
                  <p className="text-brand-muted text-sm mt-2">{partner.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section
        className="py-20 md:py-32 bg-white"
        data-testid="blog-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Il Blog
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4">
              Tasse, fisco e contabilità spiegate in modo semplice
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Card
                key={post.title}
                className="service-card overflow-hidden border-brand-border"
                data-testid={`blog-card-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-brand-muted text-sm">{post.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.metodocontabile.it/blog"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full px-8"
                data-testid="blog-cta"
              >
                Leggi il Blog
                <ExternalLink className="ml-2" size={18} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Link Utili e Strumenti */}
      <section
        className="py-20 md:py-32 bg-brand-secondary"
        data-testid="link-utili-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-brand-accent text-sm font-medium tracking-wider uppercase">
              Risorse
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-brand-foreground mt-4">
              Link Utili e Strumenti
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <a
              href="https://www.agenziaentrate.gov.it"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
              data-testid="link-agenzia-entrate"
            >
              <Card className="border-brand-border h-full transition-all duration-300 hover:border-brand-primary hover:shadow-lg hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-brand-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-primary/20 transition-colors">
                    <Landmark className="text-brand-primary" size={28} />
                  </div>
                  <h3 className="font-heading font-semibold text-brand-foreground group-hover:text-brand-primary transition-colors">
                    Agenzia delle Entrate
                  </h3>
                  <p className="text-sm text-brand-muted mt-2">Servizi fiscali online</p>
                </CardContent>
              </Card>
            </a>

            <a
              href="https://www.ilsole24ore.com/sez/norme-e-tributi"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
              data-testid="link-sole24ore"
            >
              <Card className="border-brand-border h-full transition-all duration-300 hover:border-brand-primary hover:shadow-lg hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-brand-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-primary/20 transition-colors">
                    <Newspaper className="text-brand-primary" size={28} />
                  </div>
                  <h3 className="font-heading font-semibold text-brand-foreground group-hover:text-brand-primary transition-colors">
                    Il Sole 24 Ore
                  </h3>
                  <p className="text-sm text-brand-muted mt-2">Norme e Tributi</p>
                </CardContent>
              </Card>
            </a>

            <a
              href="https://www.ud.camcom.it"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
              data-testid="link-camera-commercio"
            >
              <Card className="border-brand-border h-full transition-all duration-300 hover:border-brand-primary hover:shadow-lg hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-brand-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-primary/20 transition-colors">
                    <Building2 className="text-brand-primary" size={28} />
                  </div>
                  <h3 className="font-heading font-semibold text-brand-foreground group-hover:text-brand-primary transition-colors">
                    Camera di Commercio
                  </h3>
                  <p className="text-sm text-brand-muted mt-2">Pordenone-Udine</p>
                </CardContent>
              </Card>
            </a>

            <Link to="/risorse" className="group" data-testid="link-strumenti">
              <Card className="border-brand-border h-full transition-all duration-300 hover:border-brand-primary hover:shadow-lg hover:-translate-y-1 bg-brand-primary/5">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-brand-primary rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Calculator className="text-white" size={28} />
                  </div>
                  <h3 className="font-heading font-semibold text-brand-foreground group-hover:text-brand-primary transition-colors">
                    Calcola Imposte
                  </h3>
                  <p className="text-sm text-brand-muted mt-2">Regime Forfettario</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          <div className="text-center">
            <Link to="/risorse">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full px-8"
                data-testid="risorse-cta"
              >
                Tutti gli Strumenti e Link
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 md:py-32 bg-brand-primary"
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Hai bisogno di una consulenza?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Contattaci per una prima consulenza gratuita. Il nostro team è a tua
            disposizione per rispondere a tutte le tue domande.
          </p>
          <Link to="/contatti">
            <Button
              size="lg"
              className="bg-white text-brand-primary hover:bg-white/90 rounded-full px-10 py-6 text-lg font-medium"
              data-testid="final-cta"
            >
              Contattaci Ora
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
